namespace $ {
	type $shm_hitalama_map_runtime_sourcemap_segment = [ number ] | [ number, number, number, number ] | [ number, number, number, number, number ]
	type $shm_hitalama_map_runtime_sourcemap_line = $shm_hitalama_map_runtime_sourcemap_segment[]
	type $shm_hitalama_map_runtime_sourcemap_meta = {
		user_line: number
		user_column: number
	}

	export class $shm_hitalama_map_runtime_sourcemap extends $mol_object {

		static stack_maps = new Map< string, {
			map: any | null
			lines: $shm_hitalama_map_runtime_sourcemap_line[]
			script: string
			line_starts: number[]
			meta?: $shm_hitalama_map_runtime_sourcemap_meta
		} >()

		runtime() {
			return null as any as $shm_hitalama_map_runtime
		}

		source_meta( prefix: string ) {
			const last_line_index = prefix.lastIndexOf( '\n' )
			return {
				user_line: this.source_line_count( prefix ),
				user_column: last_line_index === -1 ? prefix.length + 1 : prefix.length - last_line_index,
			}
		}

		script_with_source_url(
			body: string,
			context: Record< string, any >,
			widget = context[ '$' + 'this_widget' ] as $shm_hitalama_map_widget | undefined,
		) {
			const source_map_match = body.match( /\n\/\/# sourceMappingURL=.*$/ )
			const source_map = source_map_match?.[0] ?? ''
			const body_without_map = source_map ? body.slice( 0, -source_map.length ) : body
			return [
				body_without_map,
				`//# sourceURL=${ this.runtime().source_url( context, 'js', widget ) }`,
				source_map,
			].filter( Boolean ).join( '\n' )
		}

		stack_map_register( script: string, meta?: $shm_hitalama_map_runtime_sourcemap_meta ) {
			const url = script.match( /^\s*\/\/# sourceURL=(\S+)\s*$/m )?.[ 1 ]
			if( !url ) return

			const map = this.source_map_decode( script )

			this.$.$shm_hitalama_map_runtime_sourcemap.stack_maps.set( url, {
				map,
				lines: map ? this.source_map_lines( map.mappings ) : [],
				script,
				line_starts: this.source_line_starts( script ),
				meta,
			} )
		}

		source_map_decode( script: string ) {
			const url = script.match( /^\s*\/\/# sourceMappingURL=(\S+)\s*$/m )?.[ 1 ]
			if( !url ) return null

			const base64 = url.match( /^data:application\/json[^,;]*;base64,(.+)$/ )?.[ 1 ]
			if( base64 ) {
				const binary = atob( base64 )
				const bytes = Uint8Array.from( binary, char => char.charCodeAt( 0 ) )
				return JSON.parse( new TextDecoder().decode( bytes ) )
			}

			const json = url.match( /^data:application\/json[^,]*,(.+)$/ )?.[ 1 ]
			if( json ) return JSON.parse( decodeURIComponent( json ) )

			return null
		}

		error_stack_remap( error: unknown ) {
			if( !( error instanceof Error ) ) return
			if( !error.stack ) return

			if( typeof error.stack === 'string' ) {
				const stack = this.stack_text_remap( error.stack )
				this.error_stack_text_remapped( error, stack )
				Object.defineProperty( error, 'stack', {
					value: stack,
					configurable: true,
					writable: true,
				} )
				return
			}

			if( Array.isArray( error.stack ) ) {
				this.stack_frames_remap( error.stack as any[] )
				this.error_stack_text_remapped( error, this.stack_frames_text( error.stack as any[] ) )
			}
		}

		error_stack_text_remapped( error: Error, stack: string ) {
			Object.defineProperty( error, '$shm_hitalama_map_runtime_stack', {
				value: stack,
				configurable: true,
				writable: true,
			} )
		}

		stack_text_remap( stack: string ) {
			return stack.replace(
				/(hitalama:\/\/runtime\/[^\s)]+\.js):(\d+):(\d+)/g,
				( frame, url: string, line: string, column: string )=> {
					const pos = this.source_position( url, Number( line ), Number( column ) )
					if( !pos ) return frame
					return `${ pos.source }:${ pos.line }:${ pos.column }`
				}
			)
		}

		stack_frames_remap( frames: any[] ) {
			for( const frame of frames ) {
				if( !frame || typeof frame !== 'object' ) continue

				const sources = [
					typeof frame.source === 'string' ? frame.source : '',
					typeof frame.eval === 'string' ? frame.eval : '',
				]

				const offset = Number( frame.offset )
				const by_offset = Number.isFinite( offset )
					? sources.reduce(
						( pos, source )=> pos ?? this.source_position_by_offset( source, offset ),
						null as ReturnType< typeof this.source_position >,
					)
					: null

				const pos_raw = frame.pos
				const by_pos = Array.isArray( pos_raw )
					? sources.reduce(
						( pos, source )=> pos ?? this.source_position(
							source,
							Number( pos_raw[ 0 ] ) + 1,
							Number( pos_raw[ 1 ] ) + 1,
						),
						null as ReturnType< typeof this.source_position >,
					)
					: null

				const pos = by_offset ?? by_pos
				if( !pos ) continue

				frame.source = pos.source
				frame.eval = ''
				frame.pos = [ pos.line - 1, pos.column - 1 ]
			}
		}

		stack_frames_text( frames: any[] ) {
			return frames.map( frame => {
				const title = frame?.function || frame?.method || '<anonymous>'
				const source = frame?.source || frame?.eval || ''
				const pos = Array.isArray( frame?.pos )
					? `:${ Number( frame.pos[ 0 ] ) + 1 }:${ Number( frame.pos[ 1 ] ) + 1 }`
					: ''

				return source ? `${ title } @ ${ source }${ pos }` : title
			} ).join( '\n' )
		}

		source_position_by_offset( url: string, offset: number ) {
			const stack_map = this.$.$shm_hitalama_map_runtime_sourcemap.stack_maps.get( url )
			if( !stack_map ) return null

			const generated = this.generated_position_by_offset( stack_map.line_starts, offset )
			return this.source_position( url, generated.line, generated.column )
		}

		generated_position_by_offset( line_starts: number[], offset: number ) {
			let low = 0
			let high = line_starts.length - 1

			while( low <= high ) {
				const mid = Math.floor(( low + high ) / 2 )
				if( line_starts[ mid ] <= offset ) low = mid + 1
				else high = mid - 1
			}

			const line_index = Math.max( 0, high )
			return {
				line: line_index + 1,
				column: offset - line_starts[ line_index ] + 1,
			}
		}

		source_line_starts( source: string ) {
			const starts = [ 0 ]
			for( let index = 0; index < source.length; ++index ) {
				if( source[ index ] === '\n' ) starts.push( index + 1 )
			}
			return starts
		}

		source_position( url: string, line: number, column: number ) {
			const stack_map = this.$.$shm_hitalama_map_runtime_sourcemap.stack_maps.get( url )
			if( !stack_map ) return null
			if( !stack_map.map ) return {
				source: url,
				... this.source_position_user( stack_map.meta, line, column ),
			}

			const segments = stack_map.lines[ line - 1 ]
			if( !segments ) return null

			const generated_column = Math.max( 0, column - 1 )
			let found = null as null | $shm_hitalama_map_runtime_sourcemap_segment
			for( const segment of segments ) {
				if( segment[ 0 ] > generated_column ) break
				if( segment.length >= 4 ) found = segment
			}
			if( !found ) return null

			const mapped = found as [ number, number, number, number, number? ]
			const source = stack_map.map.sources[ mapped[ 1 ] ]
			if( !source ) return null

			return {
				source,
				... this.source_position_user( stack_map.meta, mapped[ 2 ] + 1, mapped[ 3 ] + 1 ),
			}
		}

		source_position_user( meta: $shm_hitalama_map_runtime_sourcemap_meta | undefined, line: number, column: number ) {
			if( !meta ) return { line, column }
			if( line < meta.user_line ) return { line, column }

			return {
				line: line - meta.user_line + 1,
				column: line === meta.user_line
					? Math.max( 1, column - meta.user_column + 1 )
					: column,
			}
		}

		source_line_count( source: string ) {
			return source.split( '\n' ).length
		}

		source_map_lines( mappings: string ) {
			let index = 0
			const lines = [] as $shm_hitalama_map_runtime_sourcemap_line[]
			let line = [] as $shm_hitalama_map_runtime_sourcemap_line
			let generated_column = 0
			let source = 0
			let original_line = 0
			let original_column = 0
			let name = 0

			while( index < mappings.length ) {
				const char = mappings[ index ]

				if( char === ';' ) {
					lines.push( line )
					line = []
					index += 1
					generated_column = 0
					continue
				}

				if( char === ',' ) {
					index += 1
					continue
				}

				const segment = [] as number[]
				let value
				;[ value, index ] = this.source_map_vlq_read( mappings, index )
				generated_column += value
				segment.push( generated_column )

				if( index < mappings.length && !';,'.includes( mappings[ index ] ) ) {
					;[ value, index ] = this.source_map_vlq_read( mappings, index )
					source += value
					segment.push( source )

					;[ value, index ] = this.source_map_vlq_read( mappings, index )
					original_line += value
					segment.push( original_line )

					;[ value, index ] = this.source_map_vlq_read( mappings, index )
					original_column += value
					segment.push( original_column )

					if( index < mappings.length && !';,'.includes( mappings[ index ] ) ) {
						;[ value, index ] = this.source_map_vlq_read( mappings, index )
						name += value
						segment.push( name )
					}
				}

				line.push( segment as $shm_hitalama_map_runtime_sourcemap_segment )
			}

			lines.push( line )
			return lines
		}

		source_map_vlq_read( mappings: string, index: number ): [ number, number ] {
			let result = 0
			let shift = 0
			let digit = 0

			do {
				digit = this.source_map_base64_value( mappings[ index++ ] )
				result += ( digit & 31 ) << shift
				shift += 5
			} while( digit & 32 )

			const negative = result & 1
			result = result >> 1
			return [ negative ? -result : result, index ]
		}

		source_map_base64_value( char: string ) {
			const code = char.charCodeAt( 0 )
			if( code >= 65 && code <= 90 ) return code - 65
			if( code >= 97 && code <= 122 ) return code - 71
			if( code >= 48 && code <= 57 ) return code + 4
			if( char === '+' ) return 62
			if( char === '/' ) return 63
			throw new Error( `Bad source map char: ${ char }` )
		}

	}
}
